/* global BigInt */
import React, { useState } from 'react';
import Header from './widgets/Header';
import { Box, TextField, Button, Typography, Grid2, Paper, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

function decodeGclid(gclid) {
    function base64ToBytes(base64url) {
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
        const binary = atob(padded);
        return Uint8Array.from(binary, c => c.charCodeAt(0));
    }

    function parseProtobufForTimestamp(bytes, offset = 0, length = bytes.length) {
        while (offset < length) {
            const key = bytes[offset++];
            const fieldNumber = key >> 3;
            const wireType = key & 0x07;

            switch (wireType) {
                case 0: {
                    let value = 0n;
                    let shift = 0n;
                    let byte;
                    do {
                        byte = bytes[offset++];
                        value |= BigInt(byte & 0x7F) << shift;
                        shift += 7n;
                    } while (byte & 0x80);
                    if (fieldNumber === 1) {
                        return value;
                    }
                    break;
                }
                case 2: {
                    let len = 0;
                    let shift = 0;
                    let byte;
                    do {
                        byte = bytes[offset++];
                        len |= (byte & 0x7F) << shift;
                        shift += 7;
                    } while (byte & 0x80);

                    const end = offset + len;
                    if (end > length) return null;

                    const nested = bytes.slice(offset, end);
                    const ts = parseProtobufForTimestamp(nested);
                    if (ts !== null) return ts;

                    offset = end;
                    break;
                }
                default:
                    return null;
            }
        }
        return null;
    }

    try {
        const length = gclid.length;
        const endsWithUnderscore3Letters = /_[a-zA-Z0-9]{3}$/.test(gclid);

        if (length === 55) {
            if (!gclid.startsWith("EAIaIQobC") || !endsWithUnderscore3Letters) {
                throw new Error("invalidGclid");
            }
        } else if (length === 91) {
            if (!gclid.startsWith("CjwKCA") || !endsWithUnderscore3Letters || length > 91) {
                throw new Error("invalidGclid");
            }
        } else if (length === 92) {
            if (!gclid.startsWith("Cj0KCQ") || !endsWithUnderscore3Letters || length > 92) {
                throw new Error("invalidGclid");
            }
        } else if (length > 55 && length < 90) {
            throw new Error("invalidGclid");
        }

        const bytes = base64ToBytes(gclid);
        const timestampRaw = parseProtobufForTimestamp(bytes);

        if (!timestampRaw) throw new Error("noTimestamp");

        let timestampMs;
        let type;

        if (length === 55) {
            timestampMs = Number(timestampRaw / 1000n);
            type = 'int64-micro';
        } else {
            timestampMs = Number(timestampRaw * 1000n);
            type = 'int32-sec';
        }

        const gmtDate = new Date(timestampMs);
        const localDate = new Date(gmtDate.getTime() - gmtDate.getTimezoneOffset() * 60000);

        return {
            gclid,
            length,
            timestamp: Number(timestampRaw),
            type,
            gmt: gmtDate.toUTCString(),
            local: localDate.toString()
        };

    } catch (e) {
        return {
            gclid,
            length: gclid.length,
            error: e.message === "noTimestamp" ? "noTimestamp" : "invalidGclid"
        };
    }
}

function GClidDecoder() {
    const { t } = useTranslation();
    const [gclid, setGclid] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setResult(null);

        if (!gclid) {
            setError(t('gclidDecoder.errors.empty'));
            return;
        }

        const decoded = decodeGclid(gclid);
        if (decoded.error) {
            setError(t(`gclidDecoder.errors.${decoded.error}`));
        } else {
            setResult(decoded);
        }
    };

    return (
        <Box>
            <Header text={t('gclidDecoder.header')} />
            <Box sx={{ width: '100%', py: 4, bgcolor: '#f5f5f5', minHeight: '100vh', padding: 2 }}>
                <form onSubmit={handleSubmit}>
                    <Typography variant="subtitle1" textAlign="center" marginBottom={3}>
                        {t('gclidDecoder.description')}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            maxWidth: { xs: '100%', sm: '70%', md: '50%' },
                            mx: 'auto',
                        }}
                    >
                        <TextField
                            value={gclid}
                            onChange={(e) => setGclid(e.target.value)}
                            placeholder={t('gclidDecoder.inputPlaceholder')}
                            variant="outlined"
                            size="small"
                            required
                            fullWidth
                        />
                        <Button type="submit" variant="contained" color="primary">
                            {t('gclidDecoder.decodeButton')}
                        </Button>
                    </Box>
                </form>

                {error && (
                    <Alert severity="error" sx={{ marginTop: 2, maxWidth: 600, mx: 'auto' }}>
                        {error}
                    </Alert>
                )}

                {result && (
                    <Box sx={{ maxWidth: { xs: '80%', sm: '70%', md: '50%' }, mx: 'auto', mt: 2 }}>
                        <Grid2 container spacing={1}>
                            {[
                                { label: t('gclidDecoder.result.length'), value: result.length },
                                { label: t('gclidDecoder.result.timestamp'), value: result.timestamp },
                                { label: t('gclidDecoder.result.gmt'), value: result.gmt },
                                { label: t('gclidDecoder.result.local'), value: result.local },
                            ].map(({ label, value }) => (
                                <Grid2 item xs={12} sm={6} md={3} key={label}>
                                    <Paper sx={{ padding: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
                                        <Typography variant="body1">{value}</Typography>
                                    </Paper>
                                </Grid2>
                            ))}
                        </Grid2>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default GClidDecoder;